import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, useTheme } from '@mui/material';
import { Pie, PieChart, ResponsiveContainer } from 'recharts';

import { PieSectorDataItem } from 'recharts/types/polar/Pie';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import ArcChartTooltip from './ArcChartTooltip';
import { defaultSettings } from './ArcChartSettings';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleWidgetResponseByName } from 'system/widget/Widget';

function ArcChartContent(props: WithQueryWidgetProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const [hoveredArc, setHoveredArc] = useState<PieSectorDataItem | null>(null);
  const [chartSize, setChartSize] = useState<number>(100);
  const aggs = props.activeWidgetRequests?.[0]?.body?.aggs || {};
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'arc-chart');
  const apiResponse = apiResponseInfo?.responseBody;

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};

  let firstAggKey = Object.keys(aggs)[0];
  if (apiResponse && !apiResponse?.[firstAggKey]) {
    firstAggKey = Object.keys(apiResponse)[1];
  }

  const row = apiResponse?.[firstAggKey] || [];
  const label = row?.labels?.[0];
  const firstSeries = row?.series?.[0];
  const field_id = firstSeries?.field_id;
  const value = firstSeries?.values?.[0];
  const total = apiResponse?._total || 0;

  const secondSeries = row?.series?.[1];
  let percentage = 0;
  if (secondSeries) {
    percentage = secondSeries?.values?.[0] || 0;
  } else if (total > 0) {
    percentage = value / total;
  }

  const [drilldownIsOpen, setDrilldownIsOpen] = useState<boolean>(false);

  const displayColor = currentState.fillColor || apiResponseInfo?.originatingRequest?.queryRequestColor || theme.colors.namedColors.chart1;

  const arcData = [{
    name: i18n.t("other"),
    value: percentage !== null && percentage > 0 ? Math.round((value / percentage) - value) : 0,
    fill: theme.palette.grey[300]
  }, {
    name: label,
    value: value,
    fill: displayColor
  }];

  // Build a query that filters by the category selected
  const drilldownQuery = new Query({
      query: {
          op: "contains",
          fields: [field_id],
          value: row.keys[0]
        }
      });

  const textValue = t("intlNumber", {
    maximumFractionDigits: 0,
    roundingPriority: "lessPrecision",
    style: "percent",
    val: percentage
  });

  return (
    <>
      {!!percentage &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={`${firstAggKey}: ${label}`}
              settingsTitle={currentState.widgetTitle}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle}
            />
          }
        >
          <Tooltip
            title={(hoveredArc) ? <ArcChartTooltip arc={hoveredArc} /> : ""}
            open={Boolean(hoveredArc)}
            followCursor={true}
          >
            <Box onClick={() => setDrilldownIsOpen(true)} sx={{
              height: "100%"
            }}>
              <ResponsiveContainer
                onResize={(width, height) => setChartSize(Math.min(width, height))}>
                <PieChart style={{ cursor: "pointer" }}>
                  <Pie
                    data={arcData}
                    style={{outline: 'none'}}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    startAngle={90}
                    endAngle={450}
                    onMouseEnter={arc => setHoveredArc(arc)}
                    onMouseLeave={() => setHoveredArc(null)} />
                  <text
                    dy={chartSize * 0.06}
                    textAnchor='middle'
                    x='50%' y='50%'
                    style={{
                      fill: displayColor,
                      fontSize: chartSize * 0.2,
                      fontWeight: 'bold'
                    }}>
                    {textValue}
                  </text>
                </PieChart>
              </ResponsiveContainer>
              {drilldownIsOpen &&
                <DashboardQueryDrilldownDialog
                  title={label}
                  queryInfo={props.queryInfo}
                  onClose={() => setDrilldownIsOpen(false)}
                  activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
                  drilldownQuery={drilldownQuery}
                  widgetRequestInfo={props.widget?.request_info} />
              }
            </Box>
          </Tooltip>
        </WidgetLayout>
      }
      {!percentage &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(ArcChartContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
